import { AxiosRequestConfig, AxiosError, AxiosResponse } from "axios";
import httpService from "./httpService";
import Config from "../config";

const STORAGE_NAME = Config.STORAGE.FAILED_API_CALLS;
let onChange: (offlineCalls: number, syncing?: boolean) => void = () => {};
let isResending = false;

const readFromLocalStorage = (): Array<AxiosRequestConfig> => {
  return JSON.parse(localStorage.getItem(STORAGE_NAME) || "[]") as Array<AxiosRequestConfig>;
};

const saveToLocalStorage = (calls: Array<AxiosRequestConfig>) => {
  if (calls.length === 0) {
    localStorage.removeItem(STORAGE_NAME);
  } else {
    localStorage.setItem(STORAGE_NAME, JSON.stringify(calls));
  }
};

const clearSavedCallsFromStorage = () => {
  localStorage.removeItem(STORAGE_NAME);
};

export const add = (error: AxiosError): void => {
  // Don't add calls that are already being resent (prevent infinite loop)
  if (isResending) return;
  if (!error.config?.headers[Config.SAVE_IF_FAILED] || error.response?.status) return;

  const callsArray = readFromLocalStorage();
  callsArray.push(error.config);
  localStorage.setItem(STORAGE_NAME, JSON.stringify(callsArray));
  onChange(callsArray.length, false);
};

export const getSavedCallsCount = (): number => {
  return readFromLocalStorage().length;
};

export const resend = async (): Promise<{ success: boolean; remaining: number }> => {
  if (isResending) return { success: false, remaining: getSavedCallsCount() };

  const calls = readFromLocalStorage();
  if (calls.length === 0) return { success: true, remaining: 0 };

  isResending = true;
  onChange(calls.length, true);

  let successCount = 0;
  const failedCalls: AxiosRequestConfig[] = [];

  for (const call of calls) {
    if (!call.method || !call.url) {
      // Skip invalid calls, don't re-queue them
      continue;
    }

    try {
      // Remove saveIfFailed header to prevent re-queuing on failure during resend
      const headers = { ...call.headers };
      delete headers[Config.SAVE_IF_FAILED];

      switch (call.method) {
        case "post":
        case "put":
        case "patch":
          await httpService[call.method](call.url, call.data, { headers });
          break;
        case "delete":
          await httpService.delete(call.url, { headers });
          break;
      }
      successCount++;
    } catch (error: unknown) {
      // Only re-queue if it's a network error (no response), not an HTTP error
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        !(error as any).response?.status
      ) {
        failedCalls.push(call);
      }
      console.error("Error resending request:", error);
    }
  }

  // Save remaining failed calls back to storage
  saveToLocalStorage(failedCalls);
  isResending = false;
  onChange(failedCalls.length, false);

  return { success: failedCalls.length === 0, remaining: failedCalls.length };
};

export const onChangeListener = (callback: (offlineCalls: number, syncing?: boolean) => void) => {
  onChange = callback;
};

export default {
  add: add,
  counts: getSavedCallsCount,
  resend: resend,
  onChangeListener: onChangeListener,
};
